
import { useRef, useState, useEffect } from "react";
import { AuthStateType } from "../types/auth-state";

export function useAuthStabilization(authState: AuthStateType) {
  const { session, user, profile } = authState;
  const [isAuthStable, setIsAuthStable] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const authInitializedRef = useRef(false);
  const stableTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (stableTimeoutRef.current) {
      clearTimeout(stableTimeoutRef.current);
    }

    // If we have both session and user, auth is stable
    if (session && user) {
      console.log('[AuthStabilization] Auth is stable - session and user present');
      setIsAuthStable(true);
      
      // If we also have profile data (or confirmed no profile), we're fully ready
      if (profile !== null) {
        console.log('[AuthStabilization] Auth is ready - profile data available');
        setIsAuthReady(true);
        authInitializedRef.current = true;
      }
    } else if (!session && !user && authInitializedRef.current) {
      // Only mark as stable/ready when user explicitly signs out after being authenticated
      console.log('[AuthStabilization] User signed out, marking as stable and ready');
      setIsAuthStable(true);
      setIsAuthReady(true);
    } else if (!session && !user) {
      // Initial state - wait a moment to see if auth hydrates
      console.log('[AuthStabilization] Waiting for auth hydration...');
      stableTimeoutRef.current = setTimeout(() => {
        console.log('[AuthStabilization] Auth timeout reached - assuming no user');
        setIsAuthStable(true);
        setIsAuthReady(true);
      }, 2000); // Wait 2 seconds for auth to hydrate
    }

    return () => {
      if (stableTimeoutRef.current) {
        clearTimeout(stableTimeoutRef.current);
      }
    };
  }, [session, user, profile]);

  return {
    isAuthStable,
    isAuthReady
  };
}
